import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class CompanyService {
    private http = inject(HttpClient);
    private apiUrl = `${environment.apiUrl}/companies`;

    updateLanguage(id:number,defaultLanguage:string):Observable<{defaultLanguage:string}> {
        return this.http.patch<{defaultLanguage:string}>(`${this.apiUrl}/${id}/language`,{defaultLanguage});
    }

    getAll(): Observable<any[]> {
        return this.http.get<any[]>(this.apiUrl);
    }

    getById(id: number): Observable<any> {
        return this.http.get<any>(`${this.apiUrl}/${id}`);
    }

    getPublicBranding(id: number): Observable<any> {
        return this.http.get<any>(`${this.apiUrl}/public/${id}/branding`);
    }

    create(company: any): Observable<any> {
        return this.http.post<any>(this.apiUrl, this.normalizeCompanyColors(company));
    }

    update(id: number, company: any): Observable<any> {
        return this.http.put<any>(`${this.apiUrl}/${id}`, this.normalizeCompanyColors(company));
    }

    delete(id: number): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${id}`);
    }

    syncStripeData(id: number): Observable<any> {
        return this.http.post<any>(`${this.apiUrl}/${id}/sync-stripe`, {});
    }

    createCheckoutSession(id: number, planId?: number, billingPeriod?: string): Observable<{ checkoutUrl: string }> {
        const body = { ...(planId ? { planId } : {}), ...(billingPeriod ? { billingPeriod } : {}) };
        return this.http.post<{ checkoutUrl: string }>(`${this.apiUrl}/${id}/create-checkout-session`, body);
    }

    createEmbeddedCheckout(id: number, planId?: number, billingPeriod?: string): Observable<{ clientSecret: string }> {
        const body = { ...(planId ? { planId } : {}), ...(billingPeriod ? { billingPeriod } : {}) };
        return this.http.post<{ clientSecret: string }>(`${this.apiUrl}/${id}/create-embedded-checkout`, body);
    }

    updatePlan(id: number, planId: number, billingPeriod?: string): Observable<{ action: string; clientSecret?: string; message?: string }> {
        return this.http.post<{ action: string; clientSecret?: string; message?: string }>(
            `${this.apiUrl}/${id}/update-plan`, { planId, ...(billingPeriod ? { billingPeriod } : {}) }
        );
    }

    createBillingPortal(id: number): Observable<{ portalUrl: string }> {
        return this.http.post<{ portalUrl: string }>(`${this.apiUrl}/${id}/create-billing-portal`, {});
    }

    selectPlan(id: number, planId: number): Observable<any> {
        return this.http.post<any>(`${this.apiUrl}/${id}/select-plan`, { planId });
    }

    getTokenBalanceAmount(id: number): Observable<{ companyId: number; balance: number; includedBalance: number; purchasedBalance: number; legacyBalance: number }> {
        return this.http.get<{ companyId: number; balance: number; includedBalance: number; purchasedBalance: number; legacyBalance: number }>(`${environment.apiUrl}/api/token-balance/${id}/amount`);
    }

    /** Admin-only: directly credit tokens to a company without Stripe, creating a transaction log entry. */
    adminRefillTokens(companyId: number, amount: number, reason: string): Observable<{ companyId: number; tokensAdded: number; newBalance: number; reason: string }> {
        return this.http.post<any>(`${environment.apiUrl}/api/token-balance/${companyId}/refill`, { amount, reason });
    }

    impersonate(id: number): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/auth/impersonate/company/${id}`, {});
    }

    private normalizeCompanyColors(company: any): any {
        if (!company || typeof company !== 'object') {
            return company;
        }

        const normalized = { ...company };
        const colorFields = [
            'principalColor',
            'secondaryColor',
            'accentColor',
            'base1Color',
            'base2Color',
            'textPrimaryColor',
            'textSecondaryColor',
            'backgroundColor',
            'surfaceColor',
            'borderColor',
            'successColor',
            'warningColor',
            'errorColor',
            'infoColor'
        ];

        for (const field of colorFields) {
            if (typeof normalized[field] === 'string') {
                const trimmed = normalized[field].trim();
                normalized[field] = trimmed.length > 0 ? trimmed : null;
            }
        }

        return normalized;
    }
}
